import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { useDashboardMode } from '../context/DashboardModeContext';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const CredentialsPage = loadable(() => import(/* webpackChunkName: "CredentialsPage" */ '../containers/CredentialsPage/CredentialsPage'));
const DisputeResolutionPage = loadable(() => import(/* webpackChunkName: "DisputeResolutionPage" */ '../containers/DisputeResolutionPage/DisputeResolutionPage'));
const AdminPickupReviewPage = loadable(() => import(/* webpackChunkName: "AdminPickupReviewPage" */ '../containers/AdminPickupReviewPage/AdminPickupReviewPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const MakeOfferPage = loadable(() => import(/* webpackChunkName: "MakeOfferPage" */ '../containers/MakeOfferPage/MakeOfferPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const RentEventHomePage = loadable(() => import(/* webpackChunkName: "RentEventHomePage" */ '../containers/RentEventHomePage/RentEventHomePage'));
const VendorStarterGuidePage = loadable(() => import(/* webpackChunkName: "VendorStarterGuidePage" */ '../containers/VendorStarterGuidePage/VendorStarterGuidePage'));
const HostingGuidePage = loadable(() => import(/* webpackChunkName: "HostingGuidePage" */ '../containers/HostingGuidePage/HostingGuidePage'));
const WhatToRentOutPage = loadable(() => import(/* webpackChunkName: "WhatToRentOutPage" */ '../containers/WhatToRentOutPage/WhatToRentOutPage'));
const FreePartiesPage = loadable(() => import(/* webpackChunkName: "FreePartiesPage" */ '../containers/FreePartiesPage/FreePartiesPage'));
const ProviderDashboardPage = loadable(() => import(/* webpackChunkName: "ProviderDashboardPage" */ '../containers/ProviderDashboardPage/ProviderDashboardPage'));
const HostResourcesPage = loadable(() => import(/* webpackChunkName: "HostResourcesPage" */ '../containers/HostResourcesPage/HostResourcesPage'));
const RenterDashboardPage = loadable(() => import(/* webpackChunkName: "RenterDashboardPage" */ '../containers/RenterDashboardPage/RenterDashboardPage'));
const FavoritesPage = loadable(() => import(/* webpackChunkName: "FavoritesPage" */ '../containers/FavoritesPage/FavoritesPage'));
const BookingsPage = loadable(() => import(/* webpackChunkName: "BookingsPage" */ '../containers/BookingsPage/BookingsPage'));
const CalendarPickerPage = loadable(() => import(/* webpackChunkName: "CalendarPickerPage" */ '../containers/CalendarPickerPage/CalendarPickerPage'));
const FAQPage = loadable(() => import(/* webpackChunkName: "FAQPage" */ '../containers/FAQPage/FAQPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const ProviderSignupPage = loadable(() => import(/* webpackChunkName: "ProviderSignupPage" */ '../containers/ProviderSignupPage/ProviderSignupPage'));
// v2 handoff flow pages
const StartRentalPage = loadable(() => import(/* webpackChunkName: "StartRentalPage" */ '../containers/StartRentalPage/StartRentalPage'));
const CompleteReturnPage = loadable(() => import(/* webpackChunkName: "CompleteReturnPage" */ '../containers/CompleteReturnPage/CompleteReturnPage'));
const ReportDamagePage = loadable(() => import(/* webpackChunkName: "ReportDamagePage" */ '../containers/ReportDamagePage/ReportDamagePage'));
const ReportPickupIssuePage = loadable(() => import(/* webpackChunkName: "ReportPickupIssuePage" */ '../containers/ReportPickupIssuePage/ReportPickupIssuePage'));
const ReportIssuePage = loadable(() => import(/* webpackChunkName: "ReportIssuePage" */ '../containers/ReportIssuePage/ReportIssuePage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const ManageAccountPage = loadable(() => import(/* webpackChunkName: "ManageAccountPage" */ '../containers/ManageAccountPage/ManageAccountPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const AdminCredentialsPage = loadable(() => import(/* webpackChunkName: "AdminCredentialsPage" */ '../containers/AdminCredentialsPage/AdminCredentialsPage'));
const AdminHomePage = loadable(() => import(/* webpackChunkName: "AdminHomePage" */ '../containers/AdminHomePage/AdminHomePage'));
const RequestQuotePage = loadable(() => import(/* webpackChunkName: "RequestQuotePage" */ '../containers/RequestQuotePage/RequestQuotePage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'CredentialsPage',
  'ManageAccountPage'
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// Bare /inbox follows the user's current dashboard mode: hosts land on
// booking requests (sales), renters on their own orders. It used to
// hardcode 'sales', so a renter checking in right after paying saw the
// HOST inbox — "No conversations yet" (audit item 22).
const RedirectToInboxByMode = () => {
  const { currentMode } = useDashboardMode();
  return <NamedRedirect name="InboxPage" params={{ tab: currentMode === 'hosting' ? 'sales' : 'orders' }} />;
};

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const isSearchPageWithMap = layoutConfig.searchPage?.variantType === 'map';
  const SearchPage = isSearchPageWithMap ? SearchPageWithMap : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: RentEventHomePage,
      // Featured kits (SSR too — real product in the first paint)
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      // Stream 3 PR-5 (2026-05-15): /categories interstitial deleted
      // per Q7 lock — homepage CategoryGridSection is the top-funnel
      // entry point; renters who want to browse by category click a
      // homepage tile, which links to /s?pub_categoryLevel1=<key>.
      // We keep this route name so any stale inbound link (cached
      // bookmarks, old emails, indexed pages) lands on SearchPage
      // instead of 404'ing.
      path: '/categories',
      name: 'CategoriesPage',
      component: () => <NamedRedirect name="SearchPage" />,
    },
    {
      // Redirect /kits to search — handles Console-configured "Rentals" nav link
      path: '/kits',
      name: 'KitsRedirect',
      component: () => <NamedRedirect name="SearchPage" />,
    },
    {
      path: '/become-a-vendor/get-started',
      name: 'VendorStarterGuidePage',
      component: VendorStarterGuidePage,
    },
    {
      path: '/become-a-vendor/how-it-works',
      name: 'HostingGuidePage',
      component: HostingGuidePage,
    },
    {
      path: '/become-a-vendor/what-to-rent-out',
      name: 'WhatToRentOutPage',
      component: WhatToRentOutPage,
    },
    {
      // 5 free parties giveaway (owner request 2026-09-12). Entries go to
      // the owner's Google Form; the page closes itself after Oct 12, 2026.
      path: '/free-parties',
      name: 'FreePartiesPage',
      component: FreePartiesPage,
    },
    {
      path: '/account/dashboard',
      name: 'ProviderDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      // Consolidated: both the renter and provider dashboards now live at
      // /account/rentals. Keep this route pointing to the same component
      // so bookmarks and old links still work.
      component: props => <NamedRedirect name="RenterDashboardPage" />,
    },
    {
      path: '/account/rentals',
      name: 'RenterDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: RenterDashboardPage,
      loadData: pageDataLoadingAPI.RenterDashboardPage.loadData,
    },
    {
      // Hosting-mode dashboard. Separate URL from /account/rentals so
      // nav stays simple: no mode-aware component-swap at a shared URL,
      // each dashboard has its own route. Mode toggling between them
      // is driven by the sidebar setMode() calls, not a route wrapper.
      path: '/account/host-dashboard',
      name: 'HostDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProviderDashboardPage,
    },
    {
      // Host Resources tab: video guides + kit best practices (owner
      // request 2026-07-08, design/host-resources-and-klaviyo-sync.md).
      path: '/account/resources',
      name: 'HostResourcesPage',
      auth: true,
      authPage: 'LoginPage',
      component: HostResourcesPage,
    },
    {
      path: '/account/favorites',
      name: 'FavoritesPage',
      auth: true,
      authPage: 'LoginPage',
      component: FavoritesPage,
    },
    {
      path: '/account/bookings',
      name: 'BookingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: BookingsPage,
      loadData: pageDataLoadingAPI.BookingsPage.loadData,
    },
    {
      // Host-wide availability calendar — block dates / ranges /
      // recurring weekdays across all of the host's listings, with
      // upcoming bookings overlaid. See CalendarPickerPage.js.
      path: '/account/calendar',
      name: 'CalendarPickerPage',
      auth: true,
      authPage: 'LoginPage',
      component: CalendarPickerPage,
      loadData: pageDataLoadingAPI.CalendarPickerPage.loadData,
    },
    {
      path: '/signup/provider',
      name: 'ProviderSignupPage',
      component: ProviderSignupPage,
      loadData: pageDataLoadingAPI.ProviderSignupPage.loadData,
    },
    {
      // Gated 2026-07-02 (UI/UX audit P0-8): the page ships raw i18n keys
      // (no WhatsHotPage.* entries in en.json) and pre-launch "0 listings"
      // demand cards. Redirect to the vendor guide until it's finished —
      // route name kept so existing NamedLinks don't break.
      path: '/become-a-vendor/whats-hot',
      name: 'WhatsHotPage',
      component: () => <NamedRedirect name="VendorStarterGuidePage" />,
    },
    {
      path: '/p/faq',
      name: 'FAQPage',
      component: FAQPage,
    },
    {
      // Must come before the generic /p/:pageId CMSPage route below so
      // this hardcoded React page wins over any hosted asset of the
      // same id. See containers/AboutPage/AboutPage.js.
      path: '/p/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      // Custom contact page (replaces Sharetribe Console-managed template).
      // Text / FAQ content lives in the component file; update there to change.
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      // The OLD Console-managed contact page was still reachable at its CMS
      // URL (and in the pages sitemap) with stale copy — the in-code page
      // above is the real one (footer links /contact). Redirect, same
      // code-wins pattern as the legal pages. Found 2026-07-06 (em-dash
      // sweep verification caught the stale copy).
      path: '/p/contact',
      name: 'CMSContactRedirect',
      component: () => <NamedRedirect name="ContactPage" />,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: isSearchPageWithMap,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: isSearchPageWithMap,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/:slug/:id/make-offer',
      name: 'MakeOfferPage',
      auth: true,
      component: MakeOfferPage,
      loadData: pageDataLoadingAPI.MakeOfferPage.loadData,
    },
    {
      path: '/l/:slug/:id/request-quote',
      name: 'RequestQuotePage',
      auth: true,
      component: RequestQuotePage,
      extraProps: { mode: 'request-quote' },
      loadData: pageDataLoadingAPI.RequestQuotePage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
      // Listing form has the address autocomplete on the first
      // panel; defer-loading Mapbox here means the user can race
      // the script and trigger silent geocoder failures. Force the
      // script to load eagerly on this route.
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    {
      // Admin landing — one place for all admin tasks (owner request
      // 2026-07-02). Same gating convention as /admin/credentials:
      // auth here, isOperator() server-side on each /api/admin/*.
      path: '/admin',
      name: 'AdminHomePage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminHomePage,
    },
    {
      // Stream 2 PR-3: admin verification UI. The route itself is
      // gated by Sharetribe auth (auth: true); admin access is
      // enforced server-side by isAdmin() on each /api/admin/*
      // endpoint. The route component renders an "Access denied"
      // message when the API returns 403 — UI gating is defense in
      // depth, not the primary check.
      path: '/admin/credentials',
      name: 'AdminCredentialsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminCredentialsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: RedirectToInboxByMode,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    // ─── v2 handoff routes ─────────────────────────────────────────────
    {
      path: '/sale/:id/start-rental',
      name: 'StartRentalPage',
      auth: true,
      authPage: 'LoginPage',
      component: StartRentalPage,
      loadData: pageDataLoadingAPI.StartRentalPage.loadData,
    },
    {
      path: '/sale/:id/complete-return',
      name: 'CompleteReturnPage',
      auth: true,
      authPage: 'LoginPage',
      component: CompleteReturnPage,
      loadData: pageDataLoadingAPI.CompleteReturnPage.loadData,
    },
    {
      path: '/sale/:id/report-damage',
      name: 'ReportDamagePage',
      auth: true,
      authPage: 'LoginPage',
      component: ReportDamagePage,
      loadData: pageDataLoadingAPI.ReportDamagePage.loadData,
    },
    {
      path: '/order/:id/report-pickup-issue',
      name: 'ReportPickupIssuePage',
      auth: true,
      authPage: 'LoginPage',
      component: ReportPickupIssuePage,
      loadData: pageDataLoadingAPI.ReportPickupIssuePage.loadData,
    },
    {
      path: '/order/:id/report-issue',
      name: 'ReportIssuePage',
      auth: true,
      authPage: 'LoginPage',
      component: ReportIssuePage,
      loadData: pageDataLoadingAPI.ReportIssuePage.loadData,
    },
    {
      path: '/admin/dispute/:id',
      name: 'DisputeResolutionPage',
      auth: true,
      authPage: 'LoginPage',
      component: DisputeResolutionPage,
    },
    {
      // Review-first pickup-issue refunds (process v10): operator
      // approve / deny / resume decision page. Same gating convention
      // as the dispute page — auth here, isOperator() server-side.
      path: '/admin/pickup-review/:id',
      name: 'AdminPickupReviewPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminPickupReviewPage,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      // Credentials tab — promoted out of ProfileSettingsPage so hosts
      // can deep-link straight to credential uploads (Stream 2 PR-2
      // originally rendered the section inline under Profile).
      path: '/account/credentials',
      name: 'CredentialsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CredentialsPage,
    },
    {
      path: '/account/manage',
      name: 'ManageAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageAccountPage,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },

  ];
};

export default routeConfiguration;
